// Dashboard switch — plan §4.2. Admin ise AdminDashboard'a route'lanır;
// consultant/writer ise ConsultantDashboard içerik üretir.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatReportingPeriod,
  type ProjectsListResponse,
  type ClientsListResponse,
} from "@mahir/shared";
import { api } from "../api/client";
import { useAuthStore } from "../stores/auth-store";
import { seesAdminDashboard } from "../lib/role-ui";
import { AdminDashboard } from "./admin/AdminDashboard";

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  if (!user) return <></>;
  if (seesAdminDashboard(user)) return <AdminDashboard />;
  return <ConsultantDashboard />;
}

interface TopProject {
  id: string;
  progressPct: number;
  progressAnswered: number;
  progressTotal: number;
  dueDate: string | null;
}

function ConsultantDashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user)!;
  const canCreate = user.permissions["projects.create"] === true;

  const [projects, setProjects] = useState<ProjectsListResponse | null>(null);
  const [clientsTotal, setClientsTotal] = useState<number | null>(null);
  const [topMap, setTopMap] = useState<Map<string, TopProject>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void Promise.all([
      api<ProjectsListResponse>("/projects?limit=10&page=1"),
      api<ClientsListResponse>("/clients?limit=1").catch(() => null),
      api<{ projects: TopProject[] }>("/dashboard/top-projects?limit=10").catch(() => null),
    ])
      .then(([p, c, top]) => {
        setProjects(p);
        setClientsTotal(c?.total ?? null);
        if (top) {
          const m = new Map<string, TopProject>();
          for (const t of top.projects) m.set(t.id, t);
          setTopMap(m);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Deadline uyarı — due_date < now+7 gün: sarı; < now: kırmızı
  const deadlineWarnings = useMemo(() => {
    if (!projects)
      return [] as { id: string; clientName: string; dueDate: string; level: "red" | "yellow" }[];
    const now = Date.now();
    const weekMs = 7 * 24 * 3600 * 1000;
    return projects.projects
      .map((p) => {
        const top = topMap.get(p.id);
        if (!top?.dueDate) return null;
        if (p.status === "completed") return null;
        const due = new Date(top.dueDate).getTime();
        if (due < now) {
          return {
            id: p.id,
            clientName: p.clientName,
            dueDate: top.dueDate,
            level: "red" as const,
          };
        }
        if (due < now + weekMs) {
          return {
            id: p.id,
            clientName: p.clientName,
            dueDate: top.dueDate,
            level: "yellow" as const,
          };
        }
        return null;
      })
      .filter(
        (x): x is { id: string; clientName: string; dueDate: string; level: "red" | "yellow" } =>
          x !== null,
      );
  }, [projects, topMap]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <header className="mb-6">
        <h1
          className="text-4xl"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          {greeting}, {user.fullName}.
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--color-text-2)" }}>
          {loading
            ? "Projeleriniz yükleniyor…"
            : projects
              ? `Bu hesaba ${projects.metrics.total} proje atandı.`
              : ""}
        </p>
      </header>

      {/* Deadline uyarı şeridi — M8 §4.2.1 */}
      {deadlineWarnings.length > 0 && (
        <div
          data-alert-variant={deadlineWarnings.some((w) => w.level === "red") ? "error" : "warning"}
          className="max-w-6xl mb-4 rounded-lg px-4 py-3 flex items-center justify-between"
          style={{
            background: deadlineWarnings.some((w) => w.level === "red")
              ? "color-mix(in srgb, var(--color-error) 10%, white)"
              : "color-mix(in srgb, var(--color-warning) 8%, white)",
            border: `1px solid ${
              deadlineWarnings.some((w) => w.level === "red")
                ? "color-mix(in srgb, var(--color-error) 35%, white)"
                : "color-mix(in srgb, var(--color-warning) 40%, white)"
            }`,
          }}
        >
          <div>
            <div
              className="text-xs uppercase tracking-wider mb-1"
              style={{
                color: deadlineWarnings.some((w) => w.level === "red")
                  ? "var(--color-error)"
                  : "var(--color-warning)",
                fontWeight: 600,
              }}
            >
              Yaklaşan son teslim
            </div>
            <div className="flex gap-2 flex-wrap text-sm" style={{ color: "var(--color-text-2)" }}>
              {deadlineWarnings.slice(0, 3).map((w) => (
                <Link
                  key={w.id}
                  to={`/projects/${w.id}`}
                  className="underline"
                  style={{
                    color: w.level === "red" ? "var(--color-error)" : "var(--color-warning)",
                  }}
                >
                  {w.clientName} (
                  {new Date(w.dueDate).toLocaleDateString("tr-TR", {
                    day: "numeric",
                    month: "short",
                  })}
                  )
                </Link>
              ))}
              {deadlineWarnings.length > 3 && (
                <span style={{ color: "var(--color-text-3)" }}>
                  +{(deadlineWarnings.length - 3).toString()} daha
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Metrik kartları — §4.2.1 */}
      <div className="grid grid-cols-5 gap-3 max-w-6xl mb-6">
        <Metric
          label="Aktif Proje"
          value={projects ? projects.metrics.draft + projects.metrics.inProgress : "—"}
          tone="primary"
        />
        <Metric label="Taslak" value={projects?.metrics.draft ?? "—"} tone="muted" />
        <Metric label="Sürüyor" value={projects?.metrics.inProgress ?? "—"} tone="primary" />
        <Metric label="Tamamlanan" value={projects?.metrics.completed ?? "—"} tone="success" />
        <Metric label="Müşteri" value={clientsTotal ?? "—"} />
      </div>

      {/* Aktif projeler tablosu — son 10 */}
      <section className="max-w-6xl">
        <div className="flex items-center justify-between mb-3">
          <h2
            className="text-lg"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            Son projeler
          </h2>
          {canCreate ? (
            <Link
              to="/projects/new"
              className="px-3 py-1.5 text-sm rounded-lg font-medium"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              + Yeni Proje
            </Link>
          ) : null}
        </div>
        {projects && projects.projects.length > 0 ? (
          <div
            className="rounded-xl overflow-hidden"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            {projects.projects.map((p, i) => {
              const top = topMap.get(p.id);
              const pct = top?.progressPct ?? 0;
              const answered = top?.progressAnswered ?? 0;
              const total = top?.progressTotal ?? 0;
              const progColor =
                pct === 100
                  ? "var(--color-success)"
                  : pct > 0
                    ? "var(--color-warning)"
                    : "var(--color-text-3)";
              return (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  className="block no-underline"
                  style={{
                    padding: "12px 16px",
                    borderTop: i === 0 ? "none" : "1px solid var(--color-border)",
                    color: "var(--color-text)",
                    display: "grid",
                    gridTemplateColumns: "1fr 180px 90px 140px 110px",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500 }}>{p.clientName}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
                      {p.frameworkName} · {p.versionNumber}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "var(--color-text-2)" }}>
                    {p.assignedToName}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      fontFamily: "var(--font-mono)",
                      color: "var(--color-text-3)",
                    }}
                  >
                    {formatReportingPeriod(p.reportingYear, p.periodType)}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: progColor,
                        fontFamily: "var(--font-mono)",
                        marginBottom: 2,
                      }}
                    >
                      {total > 0
                        ? `${answered.toString()}/${total.toString()} · %${pct.toString()}`
                        : "—"}
                    </div>
                    <div
                      style={{
                        height: 3,
                        background: "var(--color-border)",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${pct.toString()}%`,
                          height: "100%",
                          background: progColor,
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color:
                          p.status === "completed"
                            ? "var(--color-success)"
                            : p.status === "in_progress"
                              ? "var(--color-primary)"
                              : "var(--color-text-3)",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {p.status === "completed"
                        ? "Tamamlandı"
                        : p.status === "in_progress"
                          ? "Sürüyor"
                          : "Taslak"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : loading ? null : (
          <div
            className="p-8 rounded-xl text-center"
            style={{
              background: "var(--color-surface)",
              border: "1px dashed var(--color-border)",
              color: "var(--color-text-3)",
            }}
          >
            Henüz proje yok.{" "}
            {canCreate ? (
              <Link to="/projects/new" style={{ color: "var(--color-primary)" }}>
                İlk projeyi oluşturun →
              </Link>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "muted" | "primary" | "success";
}): JSX.Element {
  const color =
    tone === "primary"
      ? "var(--color-primary)"
      : tone === "success"
        ? "var(--color-success)"
        : tone === "muted"
          ? "var(--color-text-3)"
          : "var(--color-text)";
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          color,
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}
